import { Link, NavLink, useNavigate } from "react-router-dom";
import { Coins, LogOut, LayoutDashboard, BarChart3, Gift, Zap } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/i18n";
import { Logo } from "@/components/Brand";
import { TOOL_LIST } from "@/lib/tools";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const LangSwitch = () => {
  const { lang, setLang } = useLang();
  return (
    <div className="flex items-center rounded-full border border-slate-200 bg-white p-0.5 font-mono text-[11px] font-semibold" data-testid="lang-switch">
      {[["en", "EN"], ["pt", "PT"]].map(([code, label]) => (
        <button
          key={code}
          onClick={() => setLang(code)}
          data-testid={`lang-${code}`}
          className={`rounded-full px-2.5 py-1 transition-colors ${lang === code ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900"}`}
        >
          {label}
        </button>
      ))}
    </div>
  );
};

const CreditBadge = ({ credits }) => {
  const { t, href } = useLang();
  return (
    <Link
      to={href("pricing")}
      data-testid="nav-credit-balance"
      className="hidden items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 font-mono text-xs font-semibold text-slate-700 transition-colors hover:border-[#00A3E0] hover:text-[#00A3E0] sm:flex"
    >
      <Coins className="h-3.5 w-3.5" /> {credits} {credits === 1 ? t("nav.credit") : t("nav.credits")}
    </Link>
  );
};

const UserMenu = ({ user, onLogout }) => {
  const { t, href } = useLang();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid="nav-user-menu" className="grid h-9 w-9 place-items-center overflow-hidden rounded-full bg-slate-900 text-sm font-semibold text-white">
          {user.picture ? <img src={user.picture} alt="" className="h-full w-full object-cover" /> : user.name?.[0]?.toUpperCase()}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <div className="px-2 py-1.5">
          <p className="text-sm font-semibold">{user.name}</p>
          <p className="truncate text-xs text-slate-500">{user.email}</p>
          <p className="mt-1 font-mono text-xs text-slate-700 sm:hidden">{user.credits} {user.credits === 1 ? t("nav.credit") : t("nav.credits")}</p>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild><Link to="/painel" data-testid="nav-menu-dashboard"><LayoutDashboard className="mr-2 h-4 w-4" />{t("nav.dashboard")}</Link></DropdownMenuItem>
        <DropdownMenuItem asChild><Link to={href("pricing")} data-testid="nav-menu-buy"><Coins className="mr-2 h-4 w-4" />{t("nav.buy")}</Link></DropdownMenuItem>
        {user.is_owner && <DropdownMenuItem asChild><Link to="/dono" data-testid="nav-menu-owner"><BarChart3 className="mr-2 h-4 w-4" />{t("owner.menu")}</Link></DropdownMenuItem>}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={onLogout} data-testid="nav-logout-button"><LogOut className="mr-2 h-4 w-4" />{t("nav.logout")}</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const PromoBar = ({ user }) => {
  const { t, href } = useLang();
  const base = "flex h-9 items-center justify-center gap-2 px-4 text-center text-xs font-semibold sm:text-sm";
  if (user === null) return <div className={`${base} bg-slate-900`} />;
  if (!user)
    return (
      <Link to="/entrar?modo=cadastro" data-testid="promo-bar" className={`${base} bg-[#00A3E0] text-white transition-colors hover:bg-[#0090c7]`}>
        <Gift className="h-4 w-4 shrink-0" /><span>{t("promo.guest")}</span><span className="hidden underline underline-offset-2 sm:inline">{t("promo.guestCta")}</span>
      </Link>
    );
  if (!user.can_use_credits)
    return (
      <Link to="/painel" data-testid="promo-bar" className={`${base} bg-sky-700 text-white`}>
        <Gift className="h-4 w-4 shrink-0" /><span>{t("promo.googleOnly")}</span>
      </Link>
    );
  const msg = user.unlimited ? t("promo.unlimited") : user.credits === 1 ? t("promo.userOne") : t("promo.user", { n: user.credits });
  return (
    <Link to={user.unlimited ? "/painel" : href("pricing")} data-testid="promo-bar" className={`${base} bg-slate-900 text-white`}>
      <Gift className="h-4 w-4 shrink-0 text-[#FFB800]" />
      <span>{msg}</span>
    </Link>
  );
};

const ToolShortcuts = () => {
  const { t, toolHref } = useLang();
  return (
    <div className="border-t border-slate-200/80 bg-slate-50/90" data-testid="tool-shortcuts">
      <div className="mx-auto flex h-12 max-w-7xl items-center gap-2 overflow-x-auto px-4 sm:px-6 lg:px-8 [scrollbar-width:none]">
        <span className="mr-1 hidden shrink-0 font-mono text-[11px] font-semibold uppercase tracking-wider text-slate-400 sm:block">
          <Zap className="mr-1 inline h-3.5 w-3.5 text-[#FFB800]" />{t("nav.quick")}
        </span>
        {TOOL_LIST.map((tl) => {
          const Icon = tl.icon;
          return (
            <NavLink
              key={tl.slug}
              to={toolHref(tl.slug)}
              data-testid={`nav-tool-${tl.slug}`}
              className={({ isActive }) => `flex shrink-0 items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-semibold transition-colors ${isActive ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-700 hover:border-slate-900 hover:text-slate-900"}`}
            >
              <span className="grid h-5 w-5 place-items-center rounded-md text-white" style={{ background: tl.color }}><Icon className="h-3 w-3" /></span>
              {t(`tools.${tl.slug}.name`)}
            </NavLink>
          );
        })}
      </div>
    </div>
  );
};

export const Navbar = () => {
  const { user, logout } = useAuth();
  const { t, href } = useLang();
  const navigate = useNavigate();
  const doLogout = async () => { await logout(); navigate(href("home")); };
  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-slate-200/80 bg-white/85 backdrop-blur-xl">
      <PromoBar user={user} />
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
        <Link to={href("home")} data-testid="nav-logo"><Logo /></Link>
        <nav className="hidden items-center gap-1 lg:flex">
          <NavLink to={href("guides")} data-testid="nav-guides" className="rounded-full px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-100">{t("nav.guides")}</NavLink>
          <NavLink to={href("pricing")} data-testid="nav-pricing" className="rounded-full px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-100">{t("nav.pricing")}</NavLink>
        </nav>
        <div className="flex shrink-0 items-center gap-2 sm:gap-3">
          <LangSwitch />
          {user ? (
            <>
              <CreditBadge credits={user.unlimited ? "∞" : user.credits} />
              <UserMenu user={user} onLogout={doLogout} />
            </>
          ) : user === false ? (
            <>
              <Link to="/entrar" data-testid="nav-login-link" className="hidden text-sm font-medium text-slate-600 hover:text-slate-900 sm:block">{t("nav.login")}</Link>
              <Button asChild className="h-9 rounded-full bg-slate-900 px-3 text-xs hover:bg-slate-800 sm:h-10 sm:px-5 sm:text-sm" data-testid="nav-signup-button">
                <Link to="/entrar?modo=cadastro">{t("nav.start")}</Link>
              </Button>
            </>
          ) : null}
        </div>
      </div>
      <ToolShortcuts />
    </header>
  );
};
